"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Timings for the progression auto-advance state machine.
 * Centralised here so the visuals (`ProgressionTransition`) and the
 * state logic (`progression-drill`) stay in sync.
 */
export const AUTO_ADVANCE_TIMINGS = {
  /** Required hold duration before entering the confirmation phase. */
  HOLD_MS: 80,
  /** Confirmation delay before triggering the visual transition. */
  CONFIRM_MS: 700,
  /** Total locked duration of the slide animation. */
  TRANSITION_TOTAL_MS: 650,
} as const;

export type AutoAdvanceState =
  | { kind: "idle" }
  | { kind: "holding"; since: number }
  | { kind: "confirming"; deadline: number }
  | { kind: "transitioning"; until: number };

interface UseAutoAdvanceParams {
  /** True when the student currently holds the full target voicing. */
  complete: boolean;
  /** Total chord count, used to wrap around at the end of the loop. */
  chordCount: number;
  /** Advances to the next step (called by the state machine). */
  onAdvance: () => void;
}

interface UseAutoAdvanceResult {
  state: AutoAdvanceState;
  /** Convenience flag : `state.kind === "confirming"`. */
  isConfirming: boolean;
  /** Convenience flag : `state.kind === "transitioning"`. */
  isTransitioning: boolean;
  /**
   * Manual override : invoked by ‹ / › / MiniStrip clicks. Skips the
   * confirmation delay and triggers an immediate visual transition.
   * Ignored while a transition is already running (no queue).
   */
  triggerManual: () => void;
  /** Reset to `idle`, used by the ↻ button. */
  reset: () => void;
}

/**
 * State machine driving the auto-advance behaviour of the progression
 * transition view.
 *
 * Lifecycle :
 *   idle → holding → confirming → transitioning → idle (or holding)
 *
 * The hook owns its timers and cleans them on unmount / state change ;
 * callers only read the resulting `state` and provide an `onAdvance`
 * callback that bumps the step.
 *
 * NB : `onAdvance` is fired immediately at the *start* of the
 * transitioning phase (t = 0). The view (`ProgressionTransition`) is
 * responsible for the actual slide animation and runs it for the full
 * `TRANSITION_TOTAL_MS` window, during which the machine ignores
 * further input and locks down to the new step.
 */
export function useAutoAdvance({
  complete,
  chordCount,
  onAdvance,
}: UseAutoAdvanceParams): UseAutoAdvanceResult {
  const [state, setState] = useState<AutoAdvanceState>({ kind: "idle" });

  // Latest values referenced by setTimeout callbacks. Refs avoid
  // re-creating timers when callers re-pass `onAdvance` on every
  // render (common with inline arrow functions).
  const onAdvanceRef = useRef(onAdvance);
  const completeRef = useRef(complete);

  useEffect(() => {
    onAdvanceRef.current = onAdvance;
  }, [onAdvance]);

  useEffect(() => {
    completeRef.current = complete;
  }, [complete]);

  // Single timer reference, the state machine only ever has one
  // pending tick. Cleared on every transition.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Cleanup on unmount.
  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  /**
   * Kick off the slide animation. Single continuous phase :
   *   t=0    → enter `transitioning`, fire `onAdvance()` (step++).
   *   t=520  → animation ends, re-evaluate the keyboard.
   *
   * The view picks up the prop change at t=0 and runs the slide on its
   * own (snapshot of previous slots vs. new ones). Idempotent w.r.t.
   * pending timers : always clears before scheduling.
   */
  const startTransition = useCallback(() => {
    clearTimer();

    setState({
      kind: "transitioning",
      until: Date.now() + AUTO_ADVANCE_TIMINGS.TRANSITION_TOTAL_MS,
    });

    onAdvanceRef.current();

    timerRef.current = setTimeout(() => {
      // Re-evaluate the keyboard at the end of the transition : if
      // the new chord is already held we re-enter `holding`
      // immediately, otherwise back to `idle`. The scheduling
      // effect below will pick up the new state and arm the right
      // timer.
      if (completeRef.current) {
        setState({ kind: "holding", since: Date.now() });
      } else {
        setState({ kind: "idle" });
      }

      timerRef.current = null;
    }, AUTO_ADVANCE_TIMINGS.TRANSITION_TOTAL_MS);
  }, [clearTimer]);

  // ─── Scheduler : derive the next transition from (state, complete) ──
  //
  // Single useEffect that owns the timers for the `holding → confirming`
  // and `confirming → transitioning` legs. Re-runs on every state /
  // `complete` change, which gives us : abort-on-release, late-arrival
  // detection (chord released after entering `transitioning`, etc.) and
  // proper handling of the «&nbsp;chord still held after the swap&nbsp;»
  // case (we re-enter `holding` from inside `startTransition`, this
  // effect then arms the next timer).
  //
  // The `transitioning` state schedules its own timers inside
  // `startTransition` and is intentionally not handled here.

  useEffect(() => {
    if (state.kind === "transitioning") {
      return;
    }

    // Chord released : roll back to idle, cancel any pending timer.
    if (!complete) {
      if (state.kind === "holding" || state.kind === "confirming") {
        clearTimer();
        setState({ kind: "idle" });
      }

      return;
    }

    // Chord held : transition to `holding` then `confirming` then
    // `transitioning`, debounced by HOLD_MS / CONFIRM_MS.
    if (state.kind === "idle") {
      clearTimer();
      setState({ kind: "holding", since: Date.now() });

      return;
    }

    if (state.kind === "holding") {
      clearTimer();

      timerRef.current = setTimeout(() => {
        setState({
          kind: "confirming",
          deadline: Date.now() + AUTO_ADVANCE_TIMINGS.CONFIRM_MS,
        });
      }, AUTO_ADVANCE_TIMINGS.HOLD_MS);

      return;
    }

    if (state.kind === "confirming") {
      clearTimer();

      timerRef.current = setTimeout(() => {
        startTransition();
      }, AUTO_ADVANCE_TIMINGS.CONFIRM_MS);

      return;
    }
  }, [complete, state.kind, clearTimer, startTransition]);

  // ─── Public API ──────────────────────────────────────────────────

  const triggerManual = useCallback(() => {
    if (state.kind === "transitioning") {
      return;
    }

    startTransition();
  }, [state.kind, startTransition]);

  const reset = useCallback(() => {
    clearTimer();
    setState({ kind: "idle" });
  }, [clearTimer]);

  // chordCount changes invalidate any in-flight transition target.
  // We don't need to do anything special here, the consumer's
  // `onAdvance` already handles the modulo, and the auto-reset on
  // `progressionId` change inside `progression-drill` will call
  // `reset()` for us.
  void chordCount;

  return {
    state,
    isConfirming: state.kind === "confirming",
    isTransitioning: state.kind === "transitioning",
    triggerManual,
    reset,
  };
}
